import CmpUtils from "@shared/utils/cmp";
import DeadmanUtils from "@shared/utils/deadman";
import DeviceConfig from "@devices/config";
import Pages from "@pages";
import { adsSelector } from "@shared/store/slices/ads.slice";
import { channelsSelector } from "@shared/store/slices/channels.slice";
import { clearStack } from "@shared/store/slices/focus.slice";
import { selectRoute } from "../../../store/slices/router.slice";
import { store } from "@shared/store/store";
import { useAppSelector } from "../../../store/hooks";
import { useCallback, useEffect, useRef, useState } from "react";

export default function useDeadman(overwrite = true) {
  const { videoAd } = useAppSelector(adsSelector);
  const { currentChannel } = useAppSelector(channelsSelector);
  const { Page } = useAppSelector(selectRoute);

  const [phase, setPhase] = useState<typeof DeadmanUtils.default | undefined>(
    DeadmanUtils.default
  );

  const adsRef = useRef(videoAd);
  const channelRef = useRef(currentChannel);
  const pageRef = useRef(Page);
  const phaseRef = useRef(phase);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  adsRef.current = videoAd;
  channelRef.current = currentChannel;
  pageRef.current = Page;
  phaseRef.current = phase;

  // Reset deadman logic

  const resetDeadman = useCallback(() => {
    updatePhase(true);
    triggerDeadman();
  }, []);

  // Deadman timeout logic

  const triggerDeadman = useCallback(() => {
    clearTimeout(timeoutRef.current);
    if (adsRef.current) return;
    const { action, timeout } = phaseRef.current || {};
    if (action && timeout) {
      timeoutRef.current = setTimeout(() => {
        if (CmpUtils.isOpen()) {
          resetDeadman();
        } else {
          updatePhase();
          action(channelRef.current);
          store.dispatch(clearStack());
        }
      }, timeout);
    }
  }, []);

  // Deadman phase logic

  const updatePhase = useCallback((interaction = false) => {
    if (adsRef.current) {
      setPhase(undefined);
      return;
    }

    switch (pageRef.current) {
      case Pages.ZAPPER:
        setPhase((prev) => {
          // If user interacts, restart zapper phase
          if (interaction) return DeadmanUtils.openModal;

          // Otherwise, move onto next phase
          switch (prev) {
            case DeadmanUtils.default:
              return DeadmanUtils.openModal;
            case DeadmanUtils.openModal:
              return DeadmanUtils.closeZapper;
          }
          return undefined;
        });
        break;
      case Pages.LANGUAGES:
      case Pages.LWA_ACCESS:
      case Pages.LWA_CONFIRM:
      case Pages.LWA_POSTAL_CODE:
      case Pages.IAP_ERROR:
      case Pages.IAP_PLANS:
      case Pages.IAP_SUCCESS:
      case Pages.IAP_UPGRADE:
      case Pages.LINK:
      case Pages.PLAYER:
      case Pages.SUCCESSFUL_LINK:
      case Pages.WELCOME:
        setPhase(undefined);
        break;
      default:
        setPhase((prev) => {
          // If user interacts, restart default phase
          if (interaction) return DeadmanUtils.default;

          // Otherwise, move onto next phase
          return prev !== DeadmanUtils.closeZapper
            ? DeadmanUtils.default
            : undefined;
        });
        break;
    }
  }, []);

  // Effect hooks

  useEffect(() => {
    if (DeviceConfig.capabilities.magicRemote) {
      document.addEventListener("mousemove", () => {
        resetDeadman();
      });
    }
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    phaseRef.current && updatePhase();
  }, [Page]);

  useEffect(() => {
    resetDeadman();
  }, [videoAd]);

  useEffect(() => {
    triggerDeadman();
  }, [phase]);

  // Return deadman functions

  return { resetDeadman, triggerDeadman };
}
